import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Play, Search, Unlink, Video, VideoOff } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import ContentStudioShell, { CatalogMediaThumbnail } from '../content/ContentStudioShell';
import { getContentDraft, listContentItems, type ContentItemSummary, type ContentTypeRef } from '../content/adminContentApi';
import type { MissionContentDocument, TextbookMediaDocument } from '../qLanguage/adminQLanguageContentApi';

const textAreaClass = 'min-h-24 w-full min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring';
const labelClass = 'grid min-w-0 gap-1.5 text-sm font-medium';

const lines = (value: string): string[] => value.split('\n').map((item) => item.trim()).filter(Boolean);
const textbookMediaType: ContentTypeRef = { applicationKey: 'q-language', contentTypeKey: 'textbook-media' };

export function MissionCatalogMediaSummary({ item }: { item: ContentItemSummary }) {
  const status = item.attributes['mediaStatus'];
  const lessonId = status === 'ATTACHED'
    ? item.attributes['mediaLessonId']
    : item.attributes['suggestedMediaLessonId'];
  const available = (status === 'ATTACHED' || status === 'AVAILABLE') && lessonId;
  return (
    <CatalogMediaThumbnail
      previewPath={available ? `/api/language-learning/library-lessons/${encodeURIComponent(lessonId)}/thumbnail` : null}
      attachedLabel={status === 'ATTACHED' ? '미션 영상 연결됨' : '연결 가능한 추천 영상'}
      missingLabel="미션 영상 미연결"
    />
  );
}

export const mediaMatchesMission = (
  document: MissionContentDocument,
  media: TextbookMediaDocument,
): boolean => {
  return document.learningLanguage === media.lesson.languageTag
    && document.learningGoal === media.lesson.category
    && document.mission.sceneKey === media.lesson.scene;
};

function MissionMediaEditor({ document, onChange }: {
  document: MissionContentDocument;
  onChange: (next: MissionContentDocument) => void;
}) {
  const [items, setItems] = useState<ContentItemSummary[]>([]);
  const [selected, setSelected] = useState<TextbookMediaDocument | null>(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void listContentItems(textbookMediaType, {
      learningLanguage: document.learningLanguage || undefined,
      mediaStatus: 'ATTACHED',
    }).then((next) => {
      if (active) setItems(next);
    }).catch((failure: unknown) => {
      if (active) setError(failure instanceof Error ? failure.message : '영상 목록을 불러오지 못했습니다.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [document.learningLanguage]);

  useEffect(() => {
    const lessonId = document.mediaLink?.lessonId;
    if (!lessonId) return;
    let active = true;
    void getContentDraft<TextbookMediaDocument>(textbookMediaType, lessonId)
      .then((view) => { if (active) setSelected(view.document); })
      .catch((failure: unknown) => {
        if (active) setError(failure instanceof Error ? failure.message : '연결 영상을 불러오지 못했습니다.');
      });
    return () => { active = false; };
  }, [document.mediaLink?.lessonId]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    return items
      .filter((item) => !needle || item.title.toLocaleLowerCase().includes(needle) || item.key.slug.includes(needle))
      .sort((left, right) => {
        const scene = document.mission.sceneKey;
        const category = document.learningGoal;
        const preferMulti = !['A1', 'A2'].includes(document.mission.cefrBand.toUpperCase());
        const leftScore = Number(left.attributes.scene === scene) * 4
          + Number(left.attributes.category === category) * 2
          + Number(left.key.slug.endsWith('-multi') === preferMulti);
        const rightScore = Number(right.attributes.scene === scene) * 4
          + Number(right.attributes.category === category) * 2
          + Number(right.key.slug.endsWith('-multi') === preferMulti);
        return rightScore - leftScore || left.key.slug.localeCompare(right.key.slug);
      });
  }, [document.learningGoal, document.mission.cefrBand, document.mission.sceneKey, items, query]);
  const recommendedLessonId = useMemo(() => filtered.find((item) => (
    item.attributes.scene === document.mission.sceneKey
      && item.attributes.category === document.learningGoal
  ))?.key.slug ?? null, [document.learningGoal, document.mission.sceneKey, filtered]);

  useEffect(() => {
    if (document.mediaLink || selected || loading || !recommendedLessonId) return;
    let active = true;
    setWorking(true);
    void getContentDraft<TextbookMediaDocument>(textbookMediaType, recommendedLessonId)
      .then((view) => { if (active) setSelected(view.document); })
      .catch((failure: unknown) => {
        if (active) setError(failure instanceof Error ? failure.message : '추천 영상을 불러오지 못했습니다.');
      })
      .finally(() => { if (active) setWorking(false); });
    return () => { active = false; };
  }, [document.mediaLink, loading, recommendedLessonId, selected]);
  const compatible = selected ? mediaMatchesMission(document, selected) : false;
  const linked = document.mediaLink;

  const open = async (lessonId: string) => {
    setWorking(true);
    setError(null);
    try {
      const view = await getContentDraft<TextbookMediaDocument>(textbookMediaType, lessonId);
      setSelected(view.document);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : '영상을 불러오지 못했습니다.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <Card id="cms-media" className="scroll-mt-32 overflow-hidden">
      <CardHeader className="pb-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Video className="h-4 w-4" />미션 영상</CardTitle>
            <CardDescription className="mt-1">이 상세 화면에서 영상을 확인하고, 같은 듣기 대본일 때만 미션 버전에 연결합니다.</CardDescription>
          </div>
          {linked ? <Badge className="gap-1"><CheckCircle2 className="h-3 w-3" />연결됨 · {linked.lessonId}</Badge> : <Badge variant="outline">미연결</Badge>}
        </div>
      </CardHeader>
      <CardContent className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(16rem,.85fr)]">
        <div className="min-w-0">
          {selected?.media ? (
            <video key={selected.media.sha256} className="aspect-video w-full rounded-xl bg-black object-contain" controls preload="metadata" playsInline src={selected.media.playbackPath}>
              <track kind="subtitles" src={selected.captions.playbackPath} srcLang={selected.lesson.languageTag} label="영상 대사" default />
            </video>
          ) : (
            <div className="flex aspect-video w-full flex-col items-center justify-center rounded-xl border border-dashed bg-muted/30 text-center">
              <Play className="h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm font-semibold">오른쪽에서 영상을 선택하세요</p>
              <p className="mt-1 text-xs text-muted-foreground">영상 자산 메뉴로 이동하지 않아도 여기서 재생할 수 있습니다.</p>
            </div>
          )}
          {selected && <div className="mt-3 rounded-lg border p-3 text-sm">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div><p className="font-semibold">{selected.lesson.title}</p><p className="text-xs text-muted-foreground">{selected.lesson.id} · {selected.lesson.gloss}</p></div>
              {compatible ? <Badge variant="outline" className="text-emerald-700">상황 일치</Badge> : <Badge variant="destructive">상황 불일치</Badge>}
            </div>
            <p className="mt-2 text-xs leading-5 text-muted-foreground">{compatible ? '학습 언어·범주·장면이 같은 상황 영상입니다. 영상 대사는 자체 WebVTT 자막으로 표시됩니다.' : '학습 언어·범주·장면이 모두 맞는 영상만 연결할 수 있습니다.'}</p>
            <div className="mt-3 grid gap-1 rounded-md bg-muted/50 p-3 text-xs leading-5" aria-label="영상 자막 대본">
              {selected.lesson.lines.map((line, index) => <p key={`${line.speaker}-${index}`}><span className="font-semibold">{line.speaker}</span> {line.text}</p>)}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" disabled={!compatible || !selected.media || linked?.lessonId === selected.lesson.id} onClick={() => {
                if (!selected.media) return;
                onChange({
                  ...document,
                  mediaLink: {
                    lessonId: selected.lesson.id,
                    scriptSha256: selected.scriptSha256,
                    videoSha256: selected.media.sha256,
                  },
                });
              }}>이 상황 영상을 미션에 연결</Button>
              {linked && <Button size="sm" variant="outline" onClick={() => onChange({ ...document, mediaLink: null })}><Unlink className="mr-1 h-3 w-3" />연결 해제</Button>}
            </div>
          </div>}
        </div>
        <div className="min-w-0">
          <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /><Input aria-label="연결할 영상 검색" className="pl-9" placeholder="영상 제목·ID 검색" value={query} onChange={(event) => setQuery(event.target.value)} /></div>
          <div className="mt-2 grid max-h-80 gap-2 overflow-y-auto pr-1">
            {loading ? <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin" /></div> : filtered.map((item) => (
              <button key={item.key.slug} type="button" onClick={() => void open(item.key.slug)} className={`min-w-0 rounded-lg border p-3 text-left transition hover:bg-muted ${selected?.lesson.id === item.key.slug ? 'border-primary bg-primary/5' : ''}`}>
                <div className="flex items-start justify-between gap-2"><span className="truncate text-sm font-semibold">{item.title}</span>{linked?.lessonId === item.key.slug && <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />}</div>
                <p className="mt-1 text-xs text-muted-foreground">{item.key.slug} · {item.attributes.category}</p>
              </button>
            ))}
          </div>
          {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
          {working && <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />영상 여는 중</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function NarrativeEditor({ document, onChange }: {
  document: MissionContentDocument;
  onChange: (next: MissionContentDocument) => void;
}) {
  const spine = document.narrativeSpine ?? {
    placeAndMoment: '', participants: ['', ''], learnerProblem: '', factsOrRequests: ['', '', ''], outcome: '', transferVariable: '',
  };
  const update = (patch: Partial<typeof spine>) => onChange({ ...document, narrativeSpine: { ...spine, ...patch } });
  return (
    <Card id="cms-narrative" className="scroll-mt-32">
      <CardHeader className="pb-4">
        <CardTitle className="text-base">한 미션의 서사 축</CardTitle>
        <CardDescription>듣기 → 핵심 표현 → 읽기·쓰기 → AI 미션에서 이 사건이 바뀌면 발행할 수 없습니다.</CardDescription>
      </CardHeader>
      <CardContent className="grid min-w-0 gap-4 md:grid-cols-2">
        <label className={labelClass}>장소와 순간<Input value={spine.placeAndMoment} onChange={(event) => update({ placeAndMoment: event.target.value })} /></label>
        <label className={labelClass}>참여자 (한 줄에 한 명)<textarea className={textAreaClass} value={spine.participants.join('\n')} onChange={(event) => update({ participants: lines(event.target.value) })} /></label>
        <label className={labelClass}>학습자가 해결할 문제<textarea className={textAreaClass} value={spine.learnerProblem} onChange={(event) => update({ learnerProblem: event.target.value })} /></label>
        <label className={labelClass}>반드시 전달할 사실·요청 3개<textarea className={textAreaClass} value={spine.factsOrRequests.join('\n')} onChange={(event) => update({ factsOrRequests: lines(event.target.value) })} /></label>
        <label className={labelClass}>대화의 결과<Input value={spine.outcome} onChange={(event) => update({ outcome: event.target.value })} /></label>
        <label className={labelClass}>전이 과제에서 바뀌는 한 요소<Input value={spine.transferVariable} onChange={(event) => update({ transferVariable: event.target.value })} /></label>
      </CardContent>
    </Card>
  );
}

function MissionEditor({ document, onChange }: {
  document: MissionContentDocument;
  onChange: (next: MissionContentDocument) => void;
}) {
  const mission = document.mission;
  const updateMission = (patch: Partial<typeof mission>) => onChange({ ...document, mission: { ...mission, ...patch } });
  const listening = mission.listening ?? { title: '', lines: [], question: { prompt: '', choices: [] } };
  const reading = mission.reading ?? { title: '', passage: '', writingPrompt: '', minCharacters: null };

  const updateTargets = (value: string) => {
    const targets = lines(value);
    const expressions = targets.map((target) => mission.expressions.find((item) => item.expression === target) ?? {
      expression: target, meaning: '', situation: '', contrasts: [],
    });
    updateMission({ targetExpressions: targets, expressions });
  };

  return (
    <div className="grid min-w-0 gap-5">
      <Card id="cms-basics" className="scroll-mt-32">
        <CardHeader className="pb-4"><CardTitle className="text-base">미션 기본 정보</CardTitle></CardHeader>
        <CardContent className="grid min-w-0 gap-4 md:grid-cols-2">
          <label className={labelClass}>학습 언어<Input value={document.learningLanguage} onChange={(event) => onChange({ ...document, learningLanguage: event.target.value })} /></label>
          <label className={labelClass}>학습 목표<Input value={document.learningGoal} onChange={(event) => onChange({ ...document, learningGoal: event.target.value })} /></label>
          <label className={labelClass}>CEFR<Input value={mission.cefrBand} onChange={(event) => updateMission({ cefrBand: event.target.value })} /></label>
          <label className={labelClass}>장면 키<Input value={mission.sceneKey ?? ''} onChange={(event) => updateMission({ sceneKey: event.target.value || null })} /></label>
          <label className={`${labelClass} md:col-span-2`}>상황<textarea className={textAreaClass} value={mission.situation} onChange={(event) => updateMission({ situation: event.target.value })} /></label>
          <label className={`${labelClass} md:col-span-2`}>학습 목표 설명<textarea className={textAreaClass} value={mission.goal} onChange={(event) => updateMission({ goal: event.target.value })} /></label>
          <label className={labelClass}>전이 과제<textarea className={textAreaClass} value={mission.transferPrompt} onChange={(event) => updateMission({ transferPrompt: event.target.value })} /></label>
          <label className={labelClass}>Can-Do 문장<textarea className={textAreaClass} value={mission.canDoStatement} onChange={(event) => updateMission({ canDoStatement: event.target.value })} /></label>
          <label className={`${labelClass} md:col-span-2`}>작성 근거 (앱에는 노출되지 않음)<textarea className={textAreaClass} value={mission.note ?? ''} onChange={(event) => updateMission({ note: event.target.value || null })} /></label>
        </CardContent>
      </Card>

      <Card id="cms-listening" className="scroll-mt-32">
        <CardHeader className="pb-4"><CardTitle className="text-base">1단계 · 듣기</CardTitle></CardHeader>
        <CardContent className="grid min-w-0 gap-4">
          <label className={labelClass}>제목<Input value={listening.title} onChange={(event) => updateMission({ listening: { ...listening, title: event.target.value } })} /></label>
          <label className={labelClass}>대화 (화자 | 대사)<textarea className={textAreaClass} value={listening.lines.map((line) => `${line.speaker} | ${line.text}`).join('\n')} onChange={(event) => updateMission({ listening: { ...listening, lines: lines(event.target.value).map((row) => { const [speaker, ...text] = row.split('|'); return { speaker: speaker.trim(), text: text.join('|').trim() }; }) } })} /></label>
          <label className={labelClass}>확인 질문<Input value={listening.question.prompt} onChange={(event) => updateMission({ listening: { ...listening, question: { ...listening.question, prompt: event.target.value } } })} /></label>
          <label className={labelClass}>선택지 (ID | 문구 | 정답 true/false)<textarea className={textAreaClass} value={listening.question.choices.map((choice) => `${choice.id} | ${choice.label} | ${choice.correct}`).join('\n')} onChange={(event) => updateMission({ listening: { ...listening, question: { ...listening.question, choices: lines(event.target.value).map((row) => { const [id, label, correct] = row.split('|').map((part) => part.trim()); return { id, label, correct: correct === 'true' }; }) } } })} /></label>
        </CardContent>
      </Card>

      <Card id="cms-expressions" className="scroll-mt-32">
        <CardHeader className="pb-4"><CardTitle className="text-base">2단계 · 핵심 표현</CardTitle></CardHeader>
        <CardContent className="grid min-w-0 gap-4">
          <label className={labelClass}>핵심 표현 (한 줄에 하나)<textarea className={textAreaClass} value={mission.targetExpressions.join('\n')} onChange={(event) => updateTargets(event.target.value)} /></label>
          {mission.expressions.map((expression, index) => (
            <div key={`${expression.expression}-${index}`} className="grid min-w-0 gap-3 rounded-lg border p-4 md:grid-cols-3">
              <label className={labelClass}>표현<Input value={expression.expression} onChange={(event) => { const next = [...mission.expressions]; next[index] = { ...expression, expression: event.target.value }; const targets = [...mission.targetExpressions]; targets[index] = event.target.value; updateMission({ expressions: next, targetExpressions: targets }); }} /></label>
              <label className={labelClass}>이 상황에서의 뜻<Input value={expression.meaning} onChange={(event) => { const next = [...mission.expressions]; next[index] = { ...expression, meaning: event.target.value }; updateMission({ expressions: next }); }} /></label>
              <label className={labelClass}>언제 쓰는지<Input value={expression.situation} onChange={(event) => { const next = [...mission.expressions]; next[index] = { ...expression, situation: event.target.value }; updateMission({ expressions: next }); }} /></label>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card id="cms-reading" className="scroll-mt-32">
        <CardHeader className="pb-4"><CardTitle className="text-base">3단계 · 읽기와 쓰기</CardTitle></CardHeader>
        <CardContent className="grid min-w-0 gap-4">
          <label className={labelClass}>제목<Input value={reading.title} onChange={(event) => updateMission({ reading: { ...reading, title: event.target.value } })} /></label>
          <label className={labelClass}>읽기 지문<textarea className={textAreaClass} value={reading.passage} onChange={(event) => updateMission({ reading: { ...reading, passage: event.target.value } })} /></label>
          <label className={labelClass}>자기 상황으로 옮겨 쓰는 과제<textarea className={textAreaClass} value={reading.writingPrompt} onChange={(event) => updateMission({ reading: { ...reading, writingPrompt: event.target.value } })} /></label>
        </CardContent>
      </Card>
    </div>
  );
}

export function MobilePreview({ document }: { document: MissionContentDocument }) {
  const mission = document.mission;
  const mediaLink = document.mediaLink;
  return (
    <div id="cms-preview" className="mx-auto w-full max-w-sm scroll-mt-32 overflow-hidden rounded-[2rem] border-8 border-slate-900 bg-background shadow-lg">
      <div className="bg-slate-900 px-5 py-2 text-center text-xs text-white">Q Language 미리보기</div>
      <div className="grid gap-4 p-5">
        <Badge variant="outline" className="w-fit">{mission.cefrBand} · {document.learningGoal}</Badge>
        {mediaLink ? <div className="overflow-hidden rounded-xl border bg-muted/40">
          <video className="aspect-video w-full bg-black object-contain" controls preload="metadata" playsInline src={`/api/language-learning/library-lessons/${encodeURIComponent(mediaLink.lessonId)}/video`}>
            <track kind="subtitles" src={`/api/language-learning/library-lessons/${encodeURIComponent(mediaLink.lessonId)}/captions.vtt`} srcLang={document.learningLanguage} label="영상 대사" default />
          </video>
          <div className="p-3"><p className="flex items-center gap-2 text-sm font-semibold"><Video className="h-4 w-4" />미션 연결 영상</p><p className="mt-1 text-xs text-muted-foreground">{mediaLink.lessonId} · 이 미션 버전에 고정</p></div>
        </div> : <div className="rounded-xl border border-dashed bg-muted/40 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold"><VideoOff className="h-4 w-4" />영상 미연결</div>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">이 미션 버전에 고정된 영상이 없습니다. 편집기에서 언어·학습 범주·장면이 같은 추천 영상을 확인한 뒤 연결하세요.</p>
        </div>}
        <div><p className="text-xs text-muted-foreground">오늘의 상황</p><h3 className="mt-1 text-lg font-semibold">{mission.situation || '상황을 입력하세요'}</h3></div>
        <div className="rounded-xl bg-muted p-4"><p className="text-xs font-semibold">학습 목표</p><p className="mt-1 text-sm">{mission.goal || '목표를 입력하세요'}</p></div>
        <div><p className="text-xs font-semibold">듣기</p>{mission.listening?.lines.map((line, index) => <p className="mt-2 text-sm" key={index}><span className="font-semibold">{line.speaker}</span> {line.text}</p>)}</div>
        <div><p className="text-xs font-semibold">핵심 표현</p><div className="mt-2 grid gap-2">{mission.targetExpressions.map((expression) => <div className="rounded-lg border px-3 py-2 text-sm" key={expression}>{expression}</div>)}</div></div>
        <div><p className="text-xs font-semibold">읽기</p><p className="mt-2 whitespace-pre-wrap text-sm leading-6">{mission.reading?.passage}</p></div>
      </div>
    </div>
  );
}

const createEmptyMission = (slug: string): MissionContentDocument => ({
  learningLanguage: 'ko',
  learningGoal: 'TRAVEL',
  narrativeSpine: {
    placeAndMoment: '',
    participants: ['', ''],
    learnerProblem: '',
    factsOrRequests: ['', '', ''],
    outcome: '',
    transferVariable: '',
  },
  mission: {
    slug,
    cefrBand: 'A2',
    sceneKey: null,
    versionNumber: 1,
    situation: '',
    goal: '',
    situationInLearningLanguage: null,
    goalInLearningLanguage: null,
    situationChunks: [],
    targetExpressions: [],
    transferPrompt: '',
    canDoStatement: '',
    expressions: [],
    listening: {
      title: '',
      lines: [],
      question: { prompt: '', choices: [] },
    },
    reading: {
      title: '',
      passage: '',
      writingPrompt: '',
      minCharacters: null,
    },
    peerScenario: null,
    note: null,
  },
  mediaLink: null,
});

const qLanguageMissionPlugin = {
  type: {
    applicationKey: 'q-language',
    contentTypeKey: 'mission',
  },
  title: 'Q Language 콘텐츠',
  description: '한 서사로 듣기부터 AI 미션까지 작성하고 검증한 뒤 불변 버전으로 발행합니다.',
  createEmptyDocument: createEmptyMission,
  getSlug: (document: MissionContentDocument) => document.mission.slug,
  renderEditor: (
    document: MissionContentDocument,
    onChange: (next: MissionContentDocument) => void,
  ) => (
    <>
      <MissionMediaEditor document={document} onChange={onChange} />
      <NarrativeEditor document={document} onChange={onChange} />
      <MissionEditor document={document} onChange={onChange} />
    </>
  ),
  renderPreview: (document: MissionContentDocument) => <MobilePreview document={document} />,
  renderFilters: (
    filter: Record<string, string | undefined>,
    onChange: (patch: Record<string, string | undefined>) => void,
  ) => (
    <div className="grid grid-cols-2 gap-2">
      <select
        className="h-9 rounded-md border bg-background px-2 text-sm"
        value={filter.learningLanguage ?? ''}
        onChange={(event) => onChange({ learningLanguage: event.target.value || undefined })}
      >
        <option value="">모든 언어</option>
        <option value="en">영어</option>
        <option value="ko">한국어</option>
      </select>
      <select
        className="h-9 rounded-md border bg-background px-2 text-sm"
        value={filter.cefrBand ?? ''}
        onChange={(event) => onChange({ cefrBand: event.target.value || undefined })}
      >
        <option value="">모든 단계</option>
        <option value="A2">A2</option>
        <option value="B1">B1</option>
      </select>
    </div>
  ),
  renderSummaryMeta: (item: ContentItemSummary) => (
    <>
      {item.attributes.learningLanguage ?? '-'} · {item.attributes.learningGoal ?? '-'} · {item.attributes.cefrBand ?? '-'}
    </>
  ),
  renderSummaryMedia: (item: ContentItemSummary) => <MissionCatalogMediaSummary item={item} />,
};

export default function QLanguageContentPage({ embedded = false }: { embedded?: boolean }) {
  return <ContentStudioShell<MissionContentDocument> plugin={qLanguageMissionPlugin} titleAs={embedded ? 'h2' : 'h1'} />;
}
